import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteRoles } from '@/hooks/useSiteRoles';
import { hasRequiredRole } from '@/lib/roleUtils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Loader2, LogOut, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { ImageItem, jsonToImageArray } from '@/lib/image-utils';
import { logStatusChange, logCarEvent } from '@/services/carHistoryService';
import { WorkflowLayout } from '@/components/layout/WorkflowLayout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { CarHistoryTimeline } from '@/components/CarHistoryTimeline';
import { WorkflowHeader } from '@/components/WorkflowHeader';
import { StepsSidebar } from '@/components/StepsSidebar';
import { WorkflowContent } from '@/components/WorkflowContent';
import { WorkflowFooter } from '@/components/WorkflowFooter';

interface Car {
  id: string;
  vin: string;
  status: string;
  current_operator_id: string | null;
  line_id: string | null;
  claimed_at: string | null;
  last_activity_at: string | null;
  item_code: string | null;
}

interface StationClaim {
  id: string;
  station_id: string;
  released_at: string | null;
  station: {
    station_number: number;
    name: string | null;
  };
}

interface WorkInstruction {
  id: string;
  title: string;
  description: string;
}

interface InstructionStep {
  id: string;
  step_number: number;
  title: string;
  description: string;
  image_url: ImageItem[];
  is_quality_check: boolean;
  evidence_required: boolean;
  parent_step_id?: string | null;
  part_number?: string | null;
  tools_equipment?: string | null;
  ppe?: string | null;
  tools_equipment_image_url?: ImageItem[];
  required_roles?: string[] | null;
}

interface StepCompletion {
  step_id: string;
  operator_id: string;
  completed_at: string;
  operator?: {
    full_name: string;
  } | null;
}

const WorkFlow = () => {
  const { carId } = useParams();
  const navigate = useNavigate();
  const { user, currentLineId, currentTenantId } = useAuth();
  const { roles, isAdmin } = useSiteRoles(currentLineId || undefined);
  const { t } = useTranslation();

  const [car, setCar] = useState<Car | null>(null);
  const [instruction, setInstruction] = useState<WorkInstruction | null>(null);
  const [steps, setSteps] = useState<InstructionStep[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentSubstepId, setCurrentSubstepId] = useState<string | null>(null);
  const [completedSteps, setCompletedSteps] = useState<Map<string, StepCompletion>>(new Map());
  const [loading, setLoading] = useState(true);
  const [showReleaseDialog, setShowReleaseDialog] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);
  const [stationClaim, setStationClaim] = useState<StationClaim | null>(null);
  const [showStationTakeoverDialog, setShowStationTakeoverDialog] = useState(false);
  const [takenOverBy, setTakenOverBy] = useState<string | null>(null);
  const [showHistorySheet, setShowHistorySheet] = useState(false);
  const [beginnerMode, setBeginnerMode] = useState(false);

  // Only top-level steps (no substeps)
  const topLevelSteps = useMemo(() => {
    return steps.filter(s => !('parent_step_id' in s) || !(s as any).parent_step_id);
  }, [steps]);

  // Organize steps into main steps and their substeps (for beginner mode)
  const stepsStructure = useMemo(() => {
    const mainSteps = steps.filter(s => !s.parent_step_id);
    return mainSteps.map(mainStep => ({
      mainStep,
      substeps: steps.filter(s => s.parent_step_id === mainStep.id),
    }));
  }, [steps]);

  // Determine current step based on beginner mode
  const currentStepInfo = useMemo(() => {
    if (beginnerMode && currentSubstepId) {
      // In beginner mode, show the selected substep
      const currentStep = steps.find(s => s.id === currentSubstepId);
      if (currentStep) {
        return currentStep;
      }
    }
    // Otherwise show the current main step
    return topLevelSteps[currentStepIndex];
  }, [beginnerMode, currentSubstepId, steps, topLevelSteps, currentStepIndex]);

  const currentStep = currentStepInfo;
  const isStepCompleted = currentStep ? completedSteps.has(currentStep.id) : false;
  const isCarOwner = car?.current_operator_id === user?.id;
  const hasStation = !!stationClaim;

  // Debug logging for ownership state
  useEffect(() => {
    console.log('[WorkFlow] Ownership state updated:', {
      carId: car?.id,
      current_operator_id: car?.current_operator_id,
      userId: user?.id,
      isCarOwner,
      hasStation,
      stationClaimId: stationClaim?.id
    });
  }, [car, user, isCarOwner, hasStation, stationClaim]);

  useEffect(() => {
    loadCarAndInstructions();
    loadStationClaim();
    const stationCleanup = subscribeToStationClaim();
    const carCleanup = subscribeToCarChanges();

    return () => {
      stationCleanup?.();
      carCleanup?.();
    };
  }, [carId, beginnerMode, currentTenantId, currentLineId]);

  // When beginner mode is enabled and substeps are loaded, set initial substep
  useEffect(() => {
    if (beginnerMode && stepsStructure.length > 0) {
      const currentMainStepStructure = stepsStructure[currentStepIndex];
      if (currentMainStepStructure && currentMainStepStructure.substeps.length > 0) {
        // Set to first substep of current main step
        setCurrentSubstepId(currentMainStepStructure.substeps[0].id);
      } else {
        // No substeps, show main step
        setCurrentSubstepId(null);
      }
    } else {
      setCurrentSubstepId(null);
    }
  }, [beginnerMode, currentStepIndex, stepsStructure]);

  // Load user's station claim
  const loadStationClaim = async () => {
    if (!user || !currentTenantId) return;

    try {
      const { data, error } = await ((supabase as any)
        .schema('mod_styling'))
        .from('station_claims')
        .select(`
          id,
          station_id,
          released_at,
          station:stations(station_number, name)
        `)
        .eq('user_id', user.id)
        .eq('tenant_id', currentTenantId)
        .is('released_at', null)
        .maybeSingle();

      if (error) throw error;
      setStationClaim(data as StationClaim | null);
    } catch (error: any) {
      console.error('Error loading station claim:', error);
    }
  };

  // Subscribe to station claim changes (detect takeover)
  const subscribeToStationClaim = () => {
    if (!user || !currentTenantId) return;

    const channel = supabase
      .channel(`station_claim_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'mod_styling',
          table: 'station_claims',
          filter: `user_id=eq.${user.id}`,
        },
        async (payload: any) => {
          // If released_at was set, someone took over my station
          if (payload.new.released_at && !payload.old.released_at) {
            // Fetch who took it over
            const { data: newClaim } = await ((supabase as any)
              .schema('mod_styling'))
              .from('station_claims')
              .select('user_id')
              .eq('station_id', payload.old.station_id)
              .eq('tenant_id', currentTenantId)
              .is('released_at', null)
              .maybeSingle();

            let newUser: string | null = null;
            if (newClaim?.user_id && currentTenantId) {
              const { data: profile } = await ((supabase as any)
                .schema('mod_styling'))
                .from('v_profiles')
                .select('full_name')
                .eq('id', newClaim.user_id)
                .eq('tenant_id', currentTenantId)
                .maybeSingle();

              newUser = profile?.full_name || null;
            }
            setTakenOverBy(newUser || t('stations.anotherOperator'));
            setStationClaim(null);
            setShowStationTakeoverDialog(true);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  // Subscribe to car changes (detect claim/release)
  const subscribeToCarChanges = () => {
    if (!carId) return;

    console.log('[WorkFlow] Setting up realtime subscription for car:', carId);

    const channel = supabase
      .channel(`car_${carId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'mod_styling',
          table: 'cars',
          filter: `id=eq.${carId}`,
        },
        (payload: any) => {
          console.log('[WorkFlow] Car update received:', {
            old_operator: payload.old?.current_operator_id,
            new_operator: payload.new?.current_operator_id,
            current_user: user?.id,
            payload
          });

          // Update local car state
          setCar(payload.new as Car);

          // Check if ownership changed
          if (payload.old?.current_operator_id !== payload.new?.current_operator_id) {
            if (payload.new?.current_operator_id === user?.id) {
              console.log('[WorkFlow] Car claimed by current user');
              toast.success(t('operator.carClaimed'));
            } else if (payload.old?.current_operator_id === user?.id) {
              console.log('[WorkFlow] Car released by current user');
            }
          }
        }
      )
      .subscribe();

    return () => {
      console.log('[WorkFlow] Cleaning up realtime subscription for car:', carId);
      supabase.removeChannel(channel);
    };
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only handle if not typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (beginnerMode) {
        // In beginner mode, navigate between substeps with left/right arrows
        const currentMainStepStructure = stepsStructure[currentStepIndex];
        if (currentMainStepStructure && currentMainStepStructure.substeps.length > 0) {
          const currentSubstepIndex = currentMainStepStructure.substeps.findIndex(
            sub => sub.id === currentSubstepId
          );

          if (e.key === 'ArrowLeft' && currentSubstepIndex > 0) {
            setCurrentSubstepId(currentMainStepStructure.substeps[currentSubstepIndex - 1].id);
          } else if (e.key === 'ArrowRight' && currentSubstepIndex < currentMainStepStructure.substeps.length - 1) {
            setCurrentSubstepId(currentMainStepStructure.substeps[currentSubstepIndex + 1].id);
          }
        }
      } else {
        // Normal mode - navigate between main steps
        if (e.key === 'ArrowLeft' && currentStepIndex > 0) {
          setCurrentStepIndex(currentStepIndex - 1);
        } else if (e.key === 'ArrowRight' && currentStepIndex < topLevelSteps.length - 1) {
          setCurrentStepIndex(currentStepIndex + 1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentStepIndex, currentSubstepId, topLevelSteps.length, beginnerMode, stepsStructure]);

  const loadCarAndInstructions = async () => {
    if (!carId || !currentTenantId) return;

    console.log('[WorkFlow] Loading car and instructions for carId:', carId);

    try {
      // Load car
      const { data: carData, error: carError } = await ((supabase as any)
        .schema('mod_styling'))
        .from('cars')
        .select('*')
        .eq('id', carId)
        .eq('tenant_id', currentTenantId)
        .single();

      if (carError) throw carError;

      console.log('[WorkFlow] Car data loaded:', {
        carId,
        current_operator_id: carData?.current_operator_id,
        userId: user?.id,
        isOwner: carData?.current_operator_id === user?.id,
        status: carData?.status
      });

      setCar(carData);

      // Load active work instruction
      // TODO: Re-enable line_id and is_global filtering once instructions are properly configured
      // Original filtering logic:
      // const instructionScope = currentLineId
      //   ? `is_global.eq.true,line_id.eq.${currentLineId}`
      //   : 'is_global.eq.true';
      // .or(instructionScope)

      // Fetch all active instructions for the tenant (treating all as global temporarily)
      const { data: allInstructions, error: instructionError } = await ((supabase as any)
        .schema('mod_styling'))
        .from('work_instructions')
        .select('*')
        .eq('active', true)
        .eq('tenant_id', currentTenantId)
        .order('created_at', { ascending: false });

      if (instructionError) throw instructionError;

      if (!allInstructions || allInstructions.length === 0) {
        toast.error(t('workflow.noInstructionsDesc'));
        setLoading(false);
        return;
      }

      // Prioritize instructions that have matching item_codes to the car's item_code
      // Instructions with matching item_codes come first, then others
      const carItemCode = carData.item_code;
      let selectedInstruction = allInstructions[0]; // default to first (most recent)

      if (carItemCode) {
        const matchingInstruction = allInstructions.find(
          (instr: any) => instr.item_codes && instr.item_codes.includes(carItemCode)
        );
        if (matchingInstruction) {
          selectedInstruction = matchingInstruction;
        }
      }

      setInstruction(selectedInstruction);

      // Load instruction steps
      // In beginner mode: load all steps (main + substeps)
      // In normal mode: load only top-level steps (parent_step_id IS NULL)
      let stepsQuery = ((supabase as any)
        .schema('mod_styling'))
        .from('instruction_steps')
        .select('*')
        .eq('instruction_id', selectedInstruction.id)
        .eq('tenant_id', currentTenantId);

      if (!beginnerMode) {
        stepsQuery = stepsQuery.is('parent_step_id', null);
      }

      const { data: stepsData, error: stepsError } = await stepsQuery
        .order('step_number', { ascending: true });

      if (stepsError) throw stepsError;

      const processedSteps = (stepsData || []).map(s => ({
        ...s,
        image_url: jsonToImageArray(s.image_url),
        tools_equipment_image_url: jsonToImageArray(s.tools_equipment_image_url),
      }));

      setSteps(processedSteps);

      // Load completions
      await loadCompletions(carId);

      setLoading(false);
    } catch (error: any) {
      toast.error(error.message || t('errors.loadData'));
      setLoading(false);
    }
  };

  const loadCompletions = async (carId: string) => {
    if (!currentTenantId) return;
    const { data } = await ((supabase as any)
      .schema('mod_styling'))
      .from('step_completions')
      .select(`
        step_id,
        operator_id,
        completed_at
      `)
      .eq('car_id', carId)
      .eq('tenant_id', currentTenantId)
      .order('completed_at', { ascending: false });

    const operatorIds = Array.from(new Set(
      (data || []).map((completion: any) => completion.operator_id).filter(Boolean)
    ));

    let operatorMap = new Map<string, string | null>();

    if (operatorIds.length > 0 && currentTenantId) {
      const { data: profiles } = await ((supabase as any)
        .schema('mod_styling'))
        .from('v_profiles')
        .select('id, full_name')
        .in('id', operatorIds)
        .eq('tenant_id', currentTenantId);

      operatorMap = new Map(
        (profiles || []).map((profile: any) => [profile.id, profile.full_name || null])
      );
    }

    // Get latest completion per step
    const completionsMap = new Map<string, StepCompletion>();
    (data || []).forEach((completion: any) => {
      if (!completionsMap.has(completion.step_id)) {
        completionsMap.set(completion.step_id, {
          ...completion,
          operator: {
            full_name: operatorMap.get(completion.operator_id) || undefined,
          },
        } as StepCompletion);
      }
    });

    setCompletedSteps(completionsMap);
  };

  const handleCompleteStep = async () => {
    if (!car || !instruction || !user || !currentStep) return;

    if (!isCarOwner) {
      toast.error(t('workflow.claimCarToComplete'));
      return;
    }

    if (!currentLineId || !currentTenantId) {
      toast.error(t('operator.noSiteSelected'));
      return;
    }

    // Check for active station claim
    if (!stationClaim) {
      toast.error(t('workflow.claimStationRedirecting'));
      setTimeout(() => navigate('/stations'), 2000);
      return;
    }

    // Role-based step completion check
    // Permission logic: Steps can define required_roles[] to restrict who can complete them
    // - If step.required_roles is null/empty → anyone can complete it
    // - If step.required_roles = ['quality'] → only quality inspectors, site_managers, and admins
    // - If step.required_roles = ['operator'] → only operators, site_managers, and admins
    // - Admins always bypass role checks
    // - User must have at least ONE of the required roles
    if (currentStep.required_roles && currentStep.required_roles.length > 0) {
      if (!hasRequiredRole(roles, currentStep.required_roles, isAdmin)) {
        toast.error(
          t('workflow.insufficientRole', {
            required: currentStep.required_roles.map(r => t(`roles.${r}`)).join(', ')
          })
        );
        return;
      }
    }

    try {
      // Insert completion (append-only) with station_id
      const { error } = await ((supabase as any)
        .schema('mod_styling'))
        .from('step_completions')
        .insert({
          tenant_id: currentTenantId,
          car_id: car.id,
          step_id: currentStep.id,
          operator_id: user.id,
          line_id: currentLineId,
          station_id: stationClaim.station_id,
        });

      if (error) throw error;

      // Update car activity
      await ((supabase as any)
        .schema('mod_styling'))
        .from('cars')
        .update({ last_activity_at: new Date().toISOString() })
        .eq('id', car.id)
        .eq('tenant_id', currentTenantId);

      // Update local state
      setCompletedSteps(new Map(completedSteps.set(currentStep.id, {
        step_id: currentStep.id,
        operator_id: user.id,
        completed_at: new Date().toISOString(),
      })));

      toast.success(t('workflow.stepCompleted'));

      // Check if all main steps are completed
      // In beginner mode, check if all substeps + main steps are completed
      let allStepsCompleted = false;

      if (beginnerMode) {
        // Check if all substeps of all main steps are completed
        allStepsCompleted = topLevelSteps.every(mainStep => {
          const substeps = steps.filter(s => s.parent_step_id === mainStep.id);
          if (substeps.length > 0) {
            // Main step has substeps - check if all substeps are completed
            return substeps.every(sub => completedSteps.has(sub.id));
          } else {
            // Main step has no substeps - check if main step is completed
            return completedSteps.has(mainStep.id);
          }
        });
      } else {
        // Normal mode - just check main steps
        allStepsCompleted = topLevelSteps.every(s =>
          s.id === currentStep.id || completedSteps.has(s.id)
        );
      }

      if (allStepsCompleted) {
        // All steps complete
        await ((supabase as any)
          .schema('mod_styling'))
          .from('cars')
          .update({
            status: 'completed',
            completed_at: new Date().toISOString(),
            last_activity_at: new Date().toISOString(),
          })
          .eq('id', car.id)
          .eq('tenant_id', currentTenantId);

        // Log status change
        if (currentLineId && currentTenantId) {
          await logStatusChange({
            carId: car.id,
            status: 'completed',
            changedBy: user.id,
            reason: 'all_steps_completed',
            tenantId: currentTenantId,
            metadata: {
              station_id: stationClaim?.station_id,
              total_steps: topLevelSteps.length,
            },
          });
        }

        toast.success(t('workflow.allCompleted'));
        setTimeout(() => navigate('/operator'), 2000);
      } else {
        // Auto-advance to next incomplete step
        if (beginnerMode) {
          // In beginner mode, advance to next incomplete substep
          const currentMainStepStructure = stepsStructure[currentStepIndex];
          if (currentMainStepStructure) {
            const nextIncompleteSubstep = currentMainStepStructure.substeps.find(
              sub => !completedSteps.has(sub.id)
            );

            if (nextIncompleteSubstep) {
              // Found next incomplete substep in current main step
              setTimeout(() => setCurrentSubstepId(nextIncompleteSubstep.id), 500);
            } else {
              // All substeps of current main step complete, move to next main step
              const nextMainStepIndex = stepsStructure.findIndex((struct, idx) => {
                if (idx <= currentStepIndex) return false;
                if (struct.substeps.length > 0) {
                  return struct.substeps.some(sub => !completedSteps.has(sub.id));
                } else {
                  return !completedSteps.has(struct.mainStep.id);
                }
              });

              if (nextMainStepIndex !== -1) {
                setTimeout(() => {
                  setCurrentStepIndex(nextMainStepIndex);
                  const nextStruct = stepsStructure[nextMainStepIndex];
                  if (nextStruct.substeps.length > 0) {
                    setCurrentSubstepId(nextStruct.substeps[0].id);
                  }
                }, 500);
              }
            }
          }
        } else {
          // Normal mode - find next incomplete main step
          const nextIncompleteIndex = topLevelSteps.findIndex((s, idx) =>
            idx > currentStepIndex && !completedSteps.has(s.id)
          );

          // Auto-advance to next incomplete step if found, otherwise stay on current
          if (nextIncompleteIndex !== -1) {
            setTimeout(() => setCurrentStepIndex(nextIncompleteIndex), 500);
          }
        }
      }
    } catch (error: any) {
      toast.error(error.message || t('errors.completeStep'));
    }
  };

  const handleReleaseCar = async () => {
    if (!car || !user) return;

    setIsReleasing(true);
    try {
      const { error } = await ((supabase as any)
        .schema('mod_styling'))
        .from('cars')
        .update({
          current_operator_id: null,
          last_activity_at: new Date().toISOString(),
        })
        .eq('id', car.id)
        .eq('tenant_id', currentTenantId);

      if (error) throw error;

      // Log car release event
      if (currentLineId && currentTenantId) {
        await logCarEvent({
          carId: car.id,
          eventType: 'car_released',
          triggeredBy: user.id,
          lineId: currentLineId,
          tenantId: currentTenantId,
          data: {
            station_id: stationClaim?.station_id,
            completed_steps: completedSteps.size,
            total_steps: topLevelSteps.length,
          },
        });
      }

      // Clear current_car_id from station_claims
      if (stationClaim) {
        const { error: stationError } = await ((supabase as any)
          .schema('mod_styling'))
          .from('station_claims')
          .update({ current_car_id: null })
          .eq('id', stationClaim.id);

        if (stationError) {
          console.error('Failed to clear car from station claim:', stationError);
          // Don't block - car release succeeded
        }
      }

      toast.success(t('workflow.carReleased'));
      navigate('/operator');
    } catch (error: any) {
      toast.error(error.message || t('workflow.failedToReleaseCar'));
      setIsReleasing(false);
    }
  };

  // Prevent navigation without releasing
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (car && car.current_operator_id === user?.id) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [car, user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!car || !instruction || topLevelSteps.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>{t('workflow.noInstructions')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              {t('workflow.noInstructionsDesc')}
            </p>
            <Button onClick={() => navigate('/operator')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('workflow.back')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Prepare steps for sidebar
  const stepsForSidebar = topLevelSteps.map((step, index) => ({
    id: step.id,
    step_number: step.step_number,
    title: step.title,
    isCompleted: completedSteps.has(step.id),
    isCurrent: index === currentStepIndex,
    substeps: beginnerMode ? steps.filter(s => s.parent_step_id === step.id).map(substep => ({
      id: substep.id,
      step_number: substep.step_number,
      title: substep.title,
      isCompleted: completedSteps.has(substep.id),
    })) : undefined,
  }));

  const completedCount = Array.from(completedSteps.keys()).filter(
    stepId => topLevelSteps.some(s => s.id === stepId)
  ).length;

  return (
    <WorkflowLayout
      header={
        <WorkflowHeader
          vin={car.vin}
          status={car.status}
          beginnerMode={beginnerMode}
          onBeginnerModeChange={setBeginnerMode}
          onReleaseClick={() => setShowReleaseDialog(true)}
          onHistoryClick={() => setShowHistorySheet(true)}
          isReleasing={isReleasing}
          stationClaim={stationClaim}
          claimedAt={car.claimed_at}
          isCarOwner={isCarOwner}
          hasStation={hasStation}
        />
      }
      sidebar={
        <StepsSidebar
          mainSteps={stepsForSidebar}
          currentMainStepIndex={currentStepIndex}
          currentSubstepId={currentSubstepId}
          beginnerMode={beginnerMode}
          onMainStepClick={(index) => setCurrentStepIndex(index)}
          onSubstepClick={(substepId) => setCurrentSubstepId(substepId)}
        />
      }
      footer={
        <WorkflowFooter
          isStepCompleted={isStepCompleted}
          isCarOwner={isCarOwner}
          hasStation={hasStation}
          onCompleteStep={handleCompleteStep}
        />
      }
    >
      <WorkflowContent
        currentStep={currentStep}
        isStepCompleted={isStepCompleted}
        stepCompletion={completedSteps.get(currentStep.id)}
        currentUserId={user?.id}
      />

      {/* Release Car Dialog */}
      <AlertDialog open={showReleaseDialog} onOpenChange={setShowReleaseDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <LogOut className="h-5 w-5 text-destructive" />
              {t('workflow.releaseVehicleTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('workflow.releaseVehicleDescription')}
              <br /><br />
              <strong>{t('workflow.progressSaved')}</strong>
              {completedCount > 0 && (
                <span> {t('workflow.youCompleted', { completedCount, totalCount: topLevelSteps.length })}</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isReleasing}>{t('workflow.stayAndContinue')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReleaseCar}
              disabled={isReleasing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isReleasing ? t('workflow.releasing') : t('workflow.releaseAndExit')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Station Takeover Dialog */}
      <AlertDialog open={showStationTakeoverDialog} onOpenChange={() => {}}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              {t('workflow.stationTakenOverTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription
              dangerouslySetInnerHTML={{
                __html: t('workflow.stationTakenOverDescription', {
                  name: takenOverBy || t('stations.anotherOperator'),
                }),
              }}
            />
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => {
                setShowStationTakeoverDialog(false);
                navigate('/stations');
              }}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {t('workflow.goToStationSelection')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* History Sheet */}
      <Sheet open={showHistorySheet} onOpenChange={setShowHistorySheet}>
        <SheetContent className="w-full sm:max-w-lg flex flex-col">
          <SheetHeader className="flex-none">
            <SheetTitle>{t('workflow.vehicleHistory')}</SheetTitle>
            <SheetDescription className="font-mono">{car.vin}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 mt-6 overflow-hidden">
            <CarHistoryTimeline carId={car.id} />
          </div>
        </SheetContent>
      </Sheet>
    </WorkflowLayout>
  );
};

export default WorkFlow;
